import React, { useState, useEffect } from "react";
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  BuildingOffice2Icon,
} from "@heroicons/react/24/outline";
import { institutionAPI } from "../../services/api";
import InstitutionView from "./modals/InstitutionView";
import Table from "../common/Table";
import EditInstitution from "./modals/EditInstitution";
import toast from "react-hot-toast";

// Status badge component
const StatusBadge = ({ status }) => {
  const statusLower = status?.toLowerCase();

  let badgeStyle = "";
  let displayText = "";

  switch (statusLower) {
    case "active":
      badgeStyle = "bg-green-100 text-green-700";
      displayText = "Active";
      break;
    case "inactive":
      badgeStyle = "bg-gray-100 text-gray-600";
      displayText = "Inactive";
      break;
    case "archived":
      badgeStyle = "bg-gray-200 text-gray-800";
      displayText = "Archived";
      break;
    case "pending":
      badgeStyle = "bg-yellow-100 text-yellow-700";
      displayText = "Pending";
      break;
    default:
      badgeStyle = "bg-gray-100 text-gray-500";
      displayText = status || "Unknown";
  }

  return (
    <span className={`px-3 py-1 text-sm rounded-full ${badgeStyle}`}>
      {displayText}
    </span>
  );
};

export default function InstitutionTable({
  data = [],
  loading = false,
  pagination = {},
  onPageChange,
  onPerPageChange,
}) {
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [institutionToEdit, setInstitutionToEdit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [institutionToDelete, setInstitutionToDelete] = useState(null);

  const [institutionTypes, setInstitutionTypes] = useState([]);
  const [institutionStatus, setInstitutionStatus] = useState([]);

  // Fetch institution types for Type filter
  useEffect(() => {
    const fetchInstitutionTypes = async () => {
      try {
        const res = await institutionAPI.getSelectInstitutionTypes();
        if (res.data.success) {
          const typesFromAPI = res.data.data || [];
          setInstitutionTypes([{ value: "all", name: "All Type" }, ...typesFromAPI]);
        }
      } catch (error) {
        console.error("Failed to fetch institution types", error);
        toast.error("Failed to load institution types");
      }
    };
    fetchInstitutionTypes();

    // Set status filter options
    setInstitutionStatus(["All Status", "Active", "Inactive", "Archived", "Pending"]);
    setStatusFilter("All Status");
  }, []);

  // Filtered data based on search, type, and status
  const filteredData = data.filter((institution) => {
    const matchesSearch =
      institution.name?.toLowerCase().includes(search.toLowerCase()) ||
      institution.institution_type?.name?.toLowerCase().includes(search.toLowerCase()) ||
      institution.address?.toLowerCase().includes(search.toLowerCase());

    const matchesType =
      typeFilter === "all" ||
      institution.institution_type?.institution_type_id === Number(typeFilter);

    const matchesStatus =
      statusFilter === "All Status" ||
      institution.status?.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesType && matchesStatus;
  });

  // Handlers
  const handleDelete = async (inst) => {
    if (window.confirm(`Are you sure you want to delete ${inst.name}?`)) {
      try {
        const response = await institutionAPI.deleteInstitution(inst.institution_id);
        if (response.data.success) {
          toast.success(`${inst.name} has been deleted.`);
          if (onPageChange) onPageChange(pagination.currentPage || 1);
        } else {
          toast.error("Failed to delete institution.");
        }
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("An error occurred while deleting.");
      }
    }
  };

  const handleView = (institution) => setSelectedInstitution(institution);
  const handleEdit = (institution) => {
    setInstitutionToEdit(institution);
    setShowEdit(true);
  };
  const handleUpdatedInstitution = () => {
    if (onPageChange) onPageChange(pagination.currentPage);
  };

  // Table columns
  const columns = [
    {
      key: "name",
      label: "Name",
      render: (_, institution) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 rounded-lg">
            <BuildingOffice2Icon className="h-6 w-6 text-blue-600" />
          </div>
          <div className="ml-4">
            <div className="text-sm text-gray-900">{institution.name}</div>
            <div className="text-sm text-gray-500">{institution.address}</div>
          </div>
        </div>
      ),
    },
    {
      key: "type",
      label: "Type",
      render: (_, institution) => (
        <div className="text-sm text-gray-900">{institution.institution_type?.name || "—"}</div>
      ),
    },
    {
      key: "contact_person",
      label: "Contact",
      render: (_, institution) => (
        <>
          <div className="text-sm text-gray-900">{institution.contact_person}</div>
          <div className="text-sm text-gray-500">{institution.contact_number}</div>
        </>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (_, institution) => <StatusBadge status={institution.status || "pending"} />,
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, institution) => (
        <div className="flex justify-left space-x-2">
          <button onClick={() => handleView(institution)} className="text-blue-600 hover:text-blue-900">
            <EyeIcon className="h-4 w-4" />
          </button>
          <button onClick={() => handleEdit(institution)} className="text-yellow-500 hover:text-yellow-700">
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              setInstitutionToDelete(institution);
              setShowDeleteModal(true);
            }}
            className="text-red-600 hover:text-red-900"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {loading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 py-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <Table
          header={
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-medium text-gray-800">Institutions</h2>
                <div className="mt-4 sm:mt-0 flex space-x-3">
                  <div className="relative w-64">
                    <input
                      type="text"
                      placeholder="Search institutions..."
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                  >
                    <FunnelIcon className="h-4 w-4 mr-1.5" />
                    Filters
                  </button>
                </div>
              </div>

              {showFilters && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Type</label>
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      {institutionTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      {institutionStatus.map((status, idx) => (
                        <option key={idx} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          }
          columns={columns}
          data={filteredData}
          getRowKey={(institution) => institution.institution_id}
          pagination={pagination}
          onPageChange={onPageChange}
          paginationProps={{
            isLoading: loading,
            onPerPageChange,
            showSizeSelector: true,
          }}
        />
      )}

      {/* Institution View Modal */}
      {selectedInstitution && (
        <InstitutionView institution={selectedInstitution} onClose={() => setSelectedInstitution(null)} />
      )}

      {/* Edit Institution Modal */}
      {showEdit && institutionToEdit && (
        <EditInstitution
          isOpen={showEdit}
          onClose={() => setShowEdit(false)}
          institution={institutionToEdit}
          onUpdated={handleUpdatedInstitution}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && institutionToDelete && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p>
              Are you sure you want to delete <b>{institutionToDelete.name}</b>?
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                onClick={async () => {
                  setShowDeleteModal(false);
                  await handleDelete(institutionToDelete);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
